import type { SailingOutlookDay } from "./detail";
import type { ConditionSafetyState } from "./safety-notes";

// Display helpers for the nightly-gap forecast (gap-forecast.ts), with type-only imports so client
// components (the sailing forecast panel, the verdict) can use them without the readers.

/** The forecast shown during the gap, for the page and its metadata (request-time only). */
export type GapForecastInfo = Readonly<{
  /** The resort's new local date the forecast is for. */
  date: string;
  /** The stored row's last write: when the forecast was issued (ISO). */
  issuedAt: string;
  /** "Forecast issued 21:56 CEST", in the resort's own zone. */
  issuedLabel: string;
}>;

/**
 * One sport's forecast for the new date: a number with its label, or a withheld label ("Closed"),
 * and a caution sentence when its day is amber, was not assessed for safety, or needs the
 * avalanche bulletin (gapForecastCaution).
 */
export type GapForecastScore = Readonly<{ score: number | null; label: string | null; caution?: string }>;

/** A forecast day's safety for one sport, as the weekly rankings read it (gap-forecast.ts). */
export type GapSafetyReading = Readonly<{ state: ConditionSafetyState | null; notes: readonly string[] }>;

/**
 * The caution shown with a gap forecast: amber with its reasons, "not assessed" when the day has
 * no clear safety assessment, and for off-piste and touring a pointer to the avalanche bulletin.
 * Undefined for a clear day of any other sport. (Red days never reach here: they give no forecast.)
 */
export function gapForecastCaution(safety: GapSafetyReading, needsBulletin = false): string | undefined {
  const parts: string[] = [];
  if (safety.state === "flagged-amber") {
    const reasons = safety.notes.slice(0, 2).join(", ");
    parts.push(reasons ? `Caution in this forecast: ${reasons}.` : "Caution flagged in this forecast.");
  } else if (safety.state !== "clear") {
    parts.push("Safety was not assessed in this forecast.");
  }
  if (needsBulletin) parts.push("Check the avalanche bulletin before heading off-piste.");
  return parts.length > 0 ? parts.join(" ") : undefined;
}

function safeTimeZone(timeZone: string | null | undefined): string {
  if (!timeZone) return "UTC";
  try {
    new Intl.DateTimeFormat("en-GB", { timeZone });
    return timeZone;
  } catch {
    return "UTC";
  }
}

/** "21:56 CEST": the issue time in the resort's zone (UTC when the zone is unknown). */
export function gapForecastIssuedTime(issuedAt: string | number, timeZone: string | null | undefined): string {
  return new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: safeTimeZone(timeZone),
    timeZoneName: "short",
  }).format(new Date(issuedAt));
}

/** "Forecast issued 21:56 CEST". */
export function gapForecastIssuedLabel(issuedAt: string | number, timeZone: string | null | undefined): string {
  return `Forecast issued ${gapForecastIssuedTime(issuedAt, timeZone)}`;
}

export function gapForecastInfo(date: string, issuedAtMs: number, timeZone: string | null | undefined): GapForecastInfo {
  return { date, issuedAt: new Date(issuedAtMs).toISOString(), issuedLabel: gapForecastIssuedLabel(issuedAtMs, timeZone) };
}

/**
 * A short caution for page metadata, from the gap forecasts' cautions (gapForecastCaution):
 * "Caution flagged." when any is amber, else "Safety not assessed." when any was not assessed,
 * plus "Check the avalanche bulletin." for an off-piste or touring forecast. Empty when none applies.
 */
export function gapCautionSummary(cautions: readonly (string | null | undefined)[], bulletin = false): string {
  const parts: string[] = [];
  if (cautions.some((caution) => caution?.startsWith("Caution"))) parts.push("Caution flagged.");
  else if (cautions.some((caution) => caution?.includes("not assessed"))) parts.push("Safety not assessed.");
  if (bulletin) parts.push("Check the avalanche bulletin.");
  return parts.join(" ");
}

/** "Saturday 3 October", for a spot-local ISO date. */
export function gapForecastDayWords(date: string): string {
  return new Date(`${date}T12:00:00Z`).toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  });
}

/**
 * A sailing category's forecast from the sailing outlook day (club or high performance): the
 * strip's stored per-day dual score, the same quantity as the headline. No number for an unscored
 * category, a category whose score is only the rough estimate (`estimated` for club,
 * `performanceEstimated` for high performance: no stored daily score), or
 * a day flagged red for the category; an amber or unassessed day carries its caution.
 */
export function sailingGapForecastScore(day: SailingOutlookDay | undefined, boat: "club" | "hp"): GapForecastScore | null {
  if (!day || (boat === "club" ? day.estimated : day.performanceEstimated)) return null;
  const score = boat === "club" ? day.score : day.performanceScore;
  if (typeof score !== "number" || !Number.isFinite(score)) return null;
  const safety = day.safety?.[boat === "club" ? "sailing-club" : "sailing-hp"];
  if (safety?.state === "flagged-red") return null;
  const caution = gapForecastCaution({ state: safety?.state ?? null, notes: safety?.notes ?? [] });
  const label = boat === "club" ? day.label : day.performanceLabel;
  return caution ? { score, label, caution } : { score, label };
}
